(() => {
  'use strict';

  const doc = document;
  const qs = (selector, root = doc) => root.querySelector(selector);
  const qsa = (selector, root = doc) => [...root.querySelectorAll(selector)];
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class KGMZoomSurface {
    constructor(viewport, surface, options = {}) {
      this.viewport = viewport;
      this.surface = surface;
      this.min = Number(options.min || 1);
      this.max = Number(options.max || 4);
      this.scale = this.min;
      this.x = 0;
      this.y = 0;
      this.pointers = new Map();
      this.dragOrigin = null;
      this.onChange = options.onChange || (() => {});
      this.wheelEnabled = options.wheel !== false;
      this.wheelNeedsActiveScene = options.wheelNeedsActiveScene === true;
      this.bind();
      this.apply(false);
    }

    dimensions() {
      const rect = this.viewport.getBoundingClientRect();
      return { rect, width: rect.width, height: rect.height };
    }

    bounds() {
      const { width, height } = this.dimensions();
      return {
        minX: Math.min(0, width - width * this.scale),
        maxX: 0,
        minY: Math.min(0, height - height * this.scale),
        maxY: 0
      };
    }

    constrain() {
      const bounds = this.bounds();
      this.x = clamp(this.x, bounds.minX, bounds.maxX);
      this.y = clamp(this.y, bounds.minY, bounds.maxY);
    }

    apply(animate = true) {
      this.constrain();
      this.surface.classList.toggle('is-animating', animate && !reducedMotion());
      this.surface.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      this.viewport.classList.toggle('is-zoomed', this.scale > this.min + 0.01);
      this.onChange(this.scale);
      if (animate) {
        window.clearTimeout(this._animationTimer);
        this._animationTimer = window.setTimeout(() => this.surface.classList.remove('is-animating'), 520);
      }
    }

    zoomAt(clientX, clientY, targetScale, animate = false) {
      const { rect } = this.dimensions();
      const next = clamp(Number(targetScale), this.min, this.max);
      const localX = clientX - rect.left;
      const localY = clientY - rect.top;
      const ratio = next / this.scale;
      this.x = localX - (localX - this.x) * ratio;
      this.y = localY - (localY - this.y) * ratio;
      this.scale = next;
      this.apply(animate);
    }

    zoomBy(delta, center = null, animate = true) {
      const { rect } = this.dimensions();
      const x = center?.x ?? rect.left + rect.width / 2;
      const y = center?.y ?? rect.top + rect.height / 2;
      this.zoomAt(x, y, this.scale + delta, animate);
    }

    focusPercent(x, y, zoom) {
      const { width, height } = this.dimensions();
      const target = clamp(Number(zoom), this.min, this.max);
      this.scale = target;
      this.x = width / 2 - (Number(x) / 100) * width * target;
      this.y = height / 2 - (Number(y) / 100) * height * target;
      this.apply(true);
    }

    reset(animate = true) {
      this.scale = this.min;
      this.x = 0;
      this.y = 0;
      this.apply(animate);
    }

    bind() {
      if (this.wheelEnabled) {
        this.viewport.addEventListener('wheel', (event) => {
          const scene = this.viewport.closest('[data-immersive-scene]');
          if (this.wheelNeedsActiveScene && scene && !scene.classList.contains('is-exploring') && !event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          const direction = event.deltaY > 0 ? -0.22 : 0.22;
          this.zoomAt(event.clientX, event.clientY, this.scale + direction, false);
        }, { passive: false });
      }

      this.viewport.addEventListener('dblclick', (event) => {
        const target = this.scale > this.min + 0.2 ? this.min : Math.min(this.max, 2.2);
        this.zoomAt(event.clientX, event.clientY, target, true);
      });

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0 && event.pointerType !== 'touch') return;
        if (event.target.closest('button, a, input, select, textarea')) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.viewport.setPointerCapture?.(event.pointerId);
        if (this.pointers.size === 1) {
          this.dragOrigin = { clientX: event.clientX, clientY: event.clientY, x: this.x, y: this.y };
        } else if (this.pointers.size === 2) {
          const points = [...this.pointers.values()];
          this.pinchOrigin = {
            distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y),
            scale: this.scale
          };
        }
        this.viewport.classList.add('is-dragging');
      });

      this.viewport.addEventListener('pointermove', (event) => {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size >= 2) {
          const points = [...this.pointers.values()].slice(0, 2);
          const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
          const center = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
          const origin = this.pinchOrigin || { distance, scale: this.scale };
          const next = origin.scale * (distance / Math.max(origin.distance, 1));
          this.zoomAt(center.x, center.y, next, false);
        } else if (this.dragOrigin && this.scale > this.min + 0.01) {
          this.x = this.dragOrigin.x + event.clientX - this.dragOrigin.clientX;
          this.y = this.dragOrigin.y + event.clientY - this.dragOrigin.clientY;
          this.apply(false);
        }
      });

      const end = (event) => {
        this.pointers.delete(event.pointerId);
        if (this.pointers.size === 1) {
          const point = [...this.pointers.values()][0];
          this.dragOrigin = { clientX: point.x, clientY: point.y, x: this.x, y: this.y };
        } else {
          this.dragOrigin = null;
          this.pinchOrigin = null;
        }
        if (!this.pointers.size) this.viewport.classList.remove('is-dragging');
      };
      this.viewport.addEventListener('pointerup', end);
      this.viewport.addEventListener('pointercancel', end);

      window.addEventListener('resize', () => this.apply(false), { passive: true });
    }
  }

  const openNativeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.body.classList.add('drawer-open');
  };

  const closeNativeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('drawer-open');
  };

  const initImmersiveScenes = (root = doc) => {
    qsa('[data-immersive-scene]', root).forEach((scene) => {
      if (scene.dataset.kgmInitialized) return;
      scene.dataset.kgmInitialized = 'true';

      const viewport = qs('[data-scene-viewport]', scene);
      const surface = qs('[data-scene-surface]', scene);
      if (!viewport || !surface) return;

      const level = qs('[data-scene-level]', scene);
      const zoom = new KGMZoomSurface(viewport, surface, {
        min: Number(scene.dataset.minZoom || 1),
        max: Number(scene.dataset.maxZoom || 4),
        wheel: scene.dataset.wheelZoom !== 'false',
        wheelNeedsActiveScene: true,
        onChange: (scale) => {
          if (level) level.textContent = `${Math.round(scale * 100)}%`;
        }
      });
      scene._kgmZoom = zoom;

      const panel = qs('[data-scene-panel]', scene);
      const items = qsa('[data-scene-panel-item]', scene);
      const setPanel = (id) => {
        let active = false;
        items.forEach((item) => {
          const match = item.id === id;
          item.hidden = !match;
          active ||= match;
        });
        panel?.classList.toggle('is-open', active);
      };

      qsa('[data-scene-hotspot]', scene).forEach((hotspot) => {
        hotspot.addEventListener('click', (event) => {
          event.stopPropagation();
          scene.classList.add('is-exploring');
          qsa('[data-scene-hotspot]', scene).forEach((item) => item.classList.toggle('is-active', item === hotspot));
          zoom.focusPercent(hotspot.dataset.focusX, hotspot.dataset.focusY, hotspot.dataset.focusZoom);
          setPanel(hotspot.dataset.panel);
        });
      });

      qs('[data-scene-panel-close]', scene)?.addEventListener('click', () => {
        panel?.classList.remove('is-open');
        items.forEach((item) => item.hidden = true);
        qsa('[data-scene-hotspot]', scene).forEach((item) => item.classList.remove('is-active'));
      });

      qs('[data-scene-zoom-in]', scene)?.addEventListener('click', () => zoom.zoomBy(0.35));
      qs('[data-scene-zoom-out]', scene)?.addEventListener('click', () => zoom.zoomBy(-0.35));
      qs('[data-scene-reset]', scene)?.addEventListener('click', () => {
        zoom.reset();
        panel?.classList.remove('is-open');
        items.forEach((item) => item.hidden = true);
        qsa('[data-scene-hotspot]', scene).forEach((item) => item.classList.remove('is-active'));
      });

      qs('[data-scene-fullscreen]', scene)?.addEventListener('click', async () => {
        try {
          if (!document.fullscreenElement) await viewport.requestFullscreen?.();
          else await document.exitFullscreen?.();
        } catch (_) {
          // Fullscreen can be blocked by embedded previews; the scene remains fully usable.
        }
      });

      qs('[data-scene-explore]', scene)?.addEventListener('click', () => {
        scene.classList.add('is-exploring');
        qs('[data-scene-instruction]', scene)?.classList.add('is-visible');
        window.setTimeout(() => qs('[data-scene-instruction]', scene)?.classList.remove('is-visible'), 3800);
      });

      qs('[data-scene-exit]', scene)?.addEventListener('click', () => {
        scene.classList.remove('is-exploring');
        zoom.reset();
        panel?.classList.remove('is-open');
        items.forEach((item) => item.hidden = true);
      });
    });
  };

  const bindZoomDialog = (dialog, viewportSelector, surfaceSelector, prefix) => {
    if (!dialog || dialog.dataset.kgmZoomBound) return null;
    dialog.dataset.kgmZoomBound = 'true';
    const viewport = qs(viewportSelector, dialog);
    const surface = qs(surfaceSelector, dialog);
    if (!viewport || !surface) return null;
    const level = qs(`[data-${prefix}-level]`, dialog);
    const zoom = new KGMZoomSurface(viewport, surface, {
      min: 1,
      max: 5,
      wheel: true,
      onChange: (scale) => {
        if (level) level.textContent = `${Math.round(scale * 100)}%`;
      }
    });
    qs(`[data-${prefix}-zoom-in]`, dialog)?.addEventListener('click', () => zoom.zoomBy(0.5));
    qs(`[data-${prefix}-zoom-out]`, dialog)?.addEventListener('click', () => zoom.zoomBy(-0.5));
    qs(`[data-${prefix}-reset]`, dialog)?.addEventListener('click', () => zoom.reset());
    dialog.addEventListener('close', () => {
      zoom.reset(false);
      document.body.classList.remove('drawer-open');
    });
    return zoom;
  };

  const initDetailLab = (root = doc) => {
    qsa('[data-detail-modal]', root).forEach((dialog) => {
      const zoom = bindZoomDialog(dialog, '[data-detail-viewport]', '.kgm-detail-modal__image', 'detail');
      qs('[data-detail-close]', dialog)?.addEventListener('click', () => closeNativeDialog(dialog));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeNativeDialog(dialog);
      });
      dialog._kgmZoom = zoom;
    });

    qsa('[data-detail-open]', root).forEach((button) => {
      if (button.dataset.kgmBound) return;
      button.dataset.kgmBound = 'true';
      button.addEventListener('pointermove', (event) => {
        const rect = button.getBoundingClientRect();
        button.style.setProperty('--detail-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        button.style.setProperty('--detail-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      });
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.detailId);
        dialog?._kgmZoom?.reset(false);
        openNativeDialog(dialog);
      });
    });
  };

  const initProductGallery = (root = doc) => {
    qsa('[data-product-gallery]', root).forEach((gallery) => {
      if (gallery.dataset.kgmInitialized) return;
      gallery.dataset.kgmInitialized = 'true';
      const slides = qsa('[data-product-slide]', gallery);
      const thumbs = qsa('[data-product-thumb]', gallery);
      const counter = qs('[data-product-current]', gallery);
      let active = 0;

      const show = (index) => {
        if (!slides.length) return;
        active = (Number(index) + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const current = i === active;
          slide.hidden = !current;
          slide.classList.toggle('is-active', current);
          if (!current) qsa('video', slide).forEach((video) => video.pause());
        });
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === active);
          thumb.setAttribute('aria-selected', i === active ? 'true' : 'false');
        });
        if (counter) counter.textContent = String(active + 1);
      };

      thumbs.forEach((thumb) => thumb.addEventListener('click', () => show(thumb.dataset.productThumb)));
      qs('[data-product-prev]', gallery)?.addEventListener('click', () => show(active - 1));
      qs('[data-product-next]', gallery)?.addEventListener('click', () => show(active + 1));
      show(0);
    });

    qsa('[data-product-image-open]', root).forEach((button) => {
      if (button.dataset.kgmBound) return;
      button.dataset.kgmBound = 'true';
      button.addEventListener('pointermove', (event) => {
        const rect = button.getBoundingClientRect();
        button.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        button.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      });
      button.addEventListener('click', () => {
        const productRoot = button.closest('[data-product-root]');
        const dialog = qs('[data-product-lightbox]', productRoot);
        const image = qs('[data-product-lightbox-image]', dialog);
        if (!dialog || !image) return;
        image.src = button.dataset.fullSrc;
        image.alt = button.dataset.alt || '';
        if (!dialog._kgmZoom) {
          dialog._kgmZoom = bindZoomDialog(dialog, '[data-product-lightbox-viewport]', '[data-product-lightbox-image]', 'product-lightbox');
          qs('[data-product-lightbox-close]', dialog)?.addEventListener('click', () => closeNativeDialog(dialog));
          dialog.addEventListener('click', (event) => {
            if (event.target === dialog) closeNativeDialog(dialog);
          });
        }
        dialog._kgmZoom?.reset(false);
        openNativeDialog(dialog);
      });
    });
  };

  const initScrollZoom = (root = doc) => {
    qsa('[data-scroll-zoom]', root).forEach((section) => {
      if (section.dataset.kgmInitialized) return;
      section.dataset.kgmInitialized = 'true';
      const media = qs('[data-scroll-zoom-media]', section);
      if (!media || reducedMotion()) return;
      const update = () => {
        const rect = section.getBoundingClientRect();
        const progress = clamp((window.innerHeight - rect.top) / (window.innerHeight + rect.height), 0, 1);
        const max = Number(section.dataset.maxScale || 1.25);
        media.style.setProperty('--scroll-scale', String(1 + (max - 1) * progress));
        media.style.setProperty('--scroll-progress', String(progress));
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    });
  };


  const initCollectionFilters = (root = doc) => {
    qsa('[data-filter-toggle]', root).forEach((button) => {
      if (button.dataset.kgmBound) return;
      button.dataset.kgmBound = 'true';
      const section = button.closest('.kgm-collection');
      const setOpen = (open) => {
        section?.classList.toggle('filters-open', open);
        document.body.classList.toggle('drawer-open', open);
      };
      button.addEventListener('click', () => setOpen(true));
      qsa('[data-filter-close]', section).forEach((close) => close.addEventListener('click', () => setOpen(false)));
    });
  };

  const initKGM = (root = doc) => {
    initImmersiveScenes(root);
    initDetailLab(root);
    initProductGallery(root);
    initScrollZoom(root);
    initCollectionFilters(root);
  };

  doc.addEventListener('DOMContentLoaded', () => initKGM());
  doc.addEventListener('shopify:section:load', (event) => initKGM(event.target));
})();